/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSuspenseQuery } from "@tanstack/react-query";

import { JSX } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import AvatarCard from "@/components/cards/AvatarCard.tsx";
import Carousel from "@/components/helpers/Carousel.tsx";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition";
import Section from "@/components/helpers/Section/Section.tsx";
import PersonContent from "@/components/persons/PersonContent";

import ContentRequests from "@/api/content/content.requests.ts";
import { Person } from "@/api/content/content.types";
import EmptyUser from "@/assets/img/EmptyActor.png";
import { ROUTES } from "@/config/routes.ts";
import { CONTENT_KEYS, useGetPersons } from "@/hooks/api/content.hooks.ts";
import { useLang } from "@/hooks/useLang";

const PersonDetailsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const lang = useLang();
  const { id } = useParams() as { id: string };
  const { data } = useSuspenseQuery<Person | null>({
    queryKey: CONTENT_KEYS.getPersonDetail(lang, parseInt(id)),
    queryFn: () => ContentRequests.getPersonDetail(parseInt(id)),
    staleTime: 1000 * 60 * 15,
  });

  const { data: persons } = useGetPersons(20);

  enum rolesMapping {
    actors = "General.actor-movies",
    director = "General.director-movies",
    producer = "General.producer-movies",
    scenario = "General.scenario-movies",
  }

  const extractContents = (data: any) => {
    if (data?.films) {
      return data.films.reduce((acc: any, role: any) => {
        acc[role.name] = role.contents;
        return acc;
      }, {});
    }

    return {
      actors: data?.actors,
      director: data?.director,
      producer: data?.producer,
      scenario: data?.scenario,
    };
  };

  const extractedData = extractContents(data);

  console.log(data);

  return (
    <>
      {data ? (
        <Helmet>
          <title>{`${data.name} | Splay`}</title>
          <meta name="og:title" content={`${data.name} | Splay`} />
        </Helmet>
      ) : null}

      <GroupTransition<HTMLDivElement> transitionKey={id}>
        {({ ref }) => (
          <div ref={ref} className={"space-y-5"}>
            {data ? (
              <section className={"flex items-center gap-5 rounded-5 max-md:py-6 max-md:pb-0 max-sm:gap-6"}>
                <img
                  src={data.picture?.small || EmptyUser}
                  alt={data.name}
                  className={"aspect-[240/311] rounded-[1.75rem] object-cover object-center  max-sm:rounded-5"}
                />

                <div>
                  <h1 className={"text-[2.25rem]/[2.75rem] font-semibold max-md:text-2xl"}>{data.name}</h1>
                  <p className="pt-2 text-[1.125rem]/[1.75rem] text-[#919090]">{data.original_name}</p>
                  <div className="mt-4 space-y-2 text-[1.125rem]">
                    <p>{data?.roles}</p>
                    <p>
                      {data?.quantity_films} {t("movies")}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}
            {Object.entries(rolesMapping).map(([key, title]) =>
              extractedData[key]?.length ? (
                <PersonContent key={key} title={t(title)} contents={extractedData[key]} />
              ) : null,
            )}
            <Section link={ROUTES.PERSONS.MAIN} title={t("General.similar-actors")} className={" py-5 max-md:py-6"}>
              <Carousel
                swiperParams={{
                  slidesPerView: 2.5,
                  slidesPerGroup: 2,
                  spaceBetween: 8,
                  breakpoints: {
                    576: { slidesPerGroup: 3, slidesPerView: 3.5 },
                    768: { slidesPerGroup: 4, slidesPerView: 4.5 },
                    992: { slidesPerGroup: 5, slidesPerView: 5.5, spaceBetween: 20 },
                    1200: { slidesPerView: 8, slidesPerGroup: 8, spaceBetween: 20 },
                    1400: { slidesPerView: 10, slidesPerGroup: 10, spaceBetween: 20 },
                    1600: { slidesPerView: 12, slidesPerGroup: 12, spaceBetween: 20 },
                  },
                }}
                items={persons.pages[0].results}
                swiperName={"actors"}
                className={"-mx-[1.875rem] max-md:-mx-6 max-md:px-6"}
                previousClassName={"top-[40%]"}
                nextClassName={"top-[40%]"}
              >
                {(item) => (
                  <AvatarCard
                    type={"round-1"}
                    link={ROUTES.PERSON_DETAILS.replace(":id", item.id.toString())}
                    imageSrc={item.picture.small}
                    name={item.name}
                  />
                )}
              </Carousel>
            </Section>
          </div>
        )}
      </GroupTransition>
    </>
  );
};

export default PersonDetailsPage;
